import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  Tooltip,
  LabelList
} from "recharts";

/**
 * Opportunity Heatmap/Bar Chart
 * 
 * Shows where AI assistance may be most relevant across:
 * - Sales & Marketing
 * - Operations
 * - Customer Experience
 * - Finance & Admin
 * - Workforce Management
 * - Compliance & Reporting
 * - IT & Systems
 */
const OpportunityChart = ({ data }) => {
  if (!data || !data.opportunities) return null;

  const chartData = data.opportunities.map(opp => ({
    area: opp.area,
    score: opp.score,
    priority: opp.priority,
    isFriction: opp.is_identified_friction
  }));

  // Gradient color based on opportunity score
  const getOpportunityColor = (score, isFriction) => {
    if (score >= 70) return isFriction ? "#d97706" : "#f59e0b"; // Gold/Amber for high
    if (score >= 40) return isFriction ? "#3b82f6" : "#60a5fa"; // Blue for medium  
    return "#64748b"; // Slate for low
  };

  return (
    <div className="w-full" data-testid="opportunity-chart">
      <div className="mb-4">
        <h3 className="font-heading text-lg font-semibold text-white print:text-slate-900">
          {data.title || "Opportunity Landscape"}
        </h3>
        <p className="text-slate-400 print:text-slate-600 text-sm">{data.subtitle}</p>
      </div>
      
      <p className="text-slate-500 print:text-slate-600 text-xs mb-4 italic">
        {data.description}
      </p>
      
      {/* Screen version */}
      <div className="h-[320px] w-full print:hidden">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData} 
            layout="vertical"
            margin={{ top: 5, right: 60, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={true} vertical={false} />
            <XAxis 
              type="number" 
              domain={[0, 100]}
              tick={{ fill: '#94a3b8', fontSize: 11 }}
              axisLine={{ stroke: '#475569' }}
            />
            <YAxis 
              type="category" 
              dataKey="area"
              tick={{ fill: '#e2e8f0', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={130}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#fff'
              }}
              formatter={(value, name, props) => [
                `${value}/100 relevance`,
                props.payload.isFriction ? 'Identified friction area' : 'General area'
              ]}
            />
            <Bar 
              dataKey="score" 
              radius={[0, 4, 4, 0]}
              barSize={22}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getOpportunityColor(entry.score, entry.isFriction)} 
                />
              ))}
              <LabelList 
                dataKey="score" 
                position="right" 
                fill="#94a3b8"
                fontSize={12}
                formatter={(value) => `${value}`}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Print version with fixed dimensions */}
      <div className="hidden print:block w-full" style={{ height: '280px' }}>
        <BarChart 
          width={450} 
          height={280} 
          data={chartData} 
          layout="vertical"
          margin={{ top: 5, right: 50, left: 10, bottom: 5 }}
          style={{ margin: '0 auto' }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" horizontal={true} vertical={false} />
          <XAxis 
            type="number" 
            domain={[0, 100]}
            tick={{ fill: '#374151', fontSize: 10 }}
            axisLine={{ stroke: '#9ca3af' }}
          />
          <YAxis 
            type="category" 
            dataKey="area"
            tick={{ fill: '#374151', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            width={120}
          />
          <Bar 
            dataKey="score" 
            radius={[0, 4, 4, 0]}
            barSize={18}
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={getOpportunityColor(entry.score, entry.isFriction)} 
              />
            ))}
            <LabelList 
              dataKey="score" 
              position="right" 
              fill="#374151"
              fontSize={10}
              formatter={(value) => `${value}`}
            />
          </Bar>
        </BarChart>
      </div>

      {/* Top Opportunities Highlight */}
      {data.top_opportunities && data.top_opportunities.length > 0 && (
        <div className="mt-4 p-4 bg-primary/10 print:bg-amber-50 rounded-lg border border-primary/30 print:border-amber-300">
          <p className="text-primary print:text-amber-800 text-xs font-medium uppercase tracking-wider mb-2">
            Areas Warranting Consideration
          </p>
          <div className="flex flex-wrap gap-2">
            {data.top_opportunities.map((opp, idx) => (
              <span 
                key={idx}
                className="px-3 py-1 bg-primary/20 print:bg-amber-100 text-primary-foreground print:text-amber-900 text-sm rounded-full"
              >
                {opp}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex justify-center gap-4 mt-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-amber-500"></div>
          <span className="text-slate-400 print:text-slate-600">High Priority</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500"></div>
          <span className="text-slate-400 print:text-slate-600">Medium Priority</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-slate-500"></div>
          <span className="text-slate-400 print:text-slate-600">Lower Priority</span>
        </div>
      </div>
    </div>
  );
};

export default OpportunityChart;
